import './styles/EventDetailView.scss';

import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { ChevronLeft, Info } from '@mui/icons-material';
import { Alert, Grid, Paper, Tab, Tabs } from '@mui/material';
import { Event, EventWaitingType, Group, ResolvedEvent, WaitingListType } from '@thro-types/events';

import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import ENROLL_MUTATION from 'gql/mutations/EventService/enrollToEvent.gql';
import UNENROLL_MUTATION from 'gql/mutations/EventService/unenrollFromEvent.gql';
import EVENT_QUERY from 'gql/queries/EventService/getEvent.gql';

import { ConfirmationDialog, GQLGuard } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import { isOrganizer } from '../helper';
import { EventEnrollmentOverview } from './components/EventEnrollmentOverview';
import { EventGroupOverview } from './components/EventGroupOverview';
import { EventOrganizationInfo } from './components/EventOrganizationInfo';

type GetEventById = { getEvent: ResolvedEvent };
export const EventDetailPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user: currentUser, authorRoles } = useAuthContext();

  const [markedGroup, setMarkedGroup] = useState<number | null>(null);
  const [groupRef, setGroupRef] = useState<Group | undefined>(undefined);
  const [enrollingState, setEnrollingState] = useState<'ENROLLING' | 'UNENROLLING' | undefined>(
    undefined
  );
  const [enrollingType, setEnrollingType] = useState<WaitingListType | undefined>(undefined);

  const getEvent = useQuery<GetEventById>(EVENT_QUERY, {
    context: { clientName: 'federation' },
    variables: { id: parseInt(id!, 10) }
  });

  usePageHeading(
    {
      heading: getEvent.data?.getEvent.name || t(translations.common.loading()),
      headingProps: { back: location.state?.back ?? `/events` }
    },
    [getEvent.data]
  );

  const { refetch } = getEvent;

  const [enroll] = useMutation(ENROLL_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [unEnroll] = useMutation(UNENROLL_MUTATION, {
    context: { clientName: 'federation' }
  });

  const handleEnrollment = async (
    event: Event | undefined,
    group: Group | undefined,
    enrollmentId?: number | undefined
  ) => {
    try {
      if (!enrollmentId) {
        const { data } = await enroll({
          variables: {
            refId:
              enrollingType === WaitingListType.GROUP
                ? group?.id
                : enrollingType === WaitingListType.EVENT
                ? event?.id
                : undefined,
            userId: currentUser.id,
            waitingListType: enrollingType !== undefined && enrollingType
          },
          refetchQueries: ['GetEvent']
        });
        if (data.enroll.waitingListId) {
          if (
            event?.priorizationType === 'SEMESTER' ||
            group?.event?.priorizationType === 'SEMESTER'
          ) {
            dispatch(
              AlertActions.success(
                t(translations.pages.events.alerts.successfulEnrollmentToWaitingListForSemester())
              )
            );
          } else {
            dispatch(
              AlertActions.success(
                t(translations.pages.events.alerts.successfulEnrollmentToWaitingList())
              )
            );
          }
        } else {
          dispatch(
            AlertActions.success(t(translations.pages.events.alerts.successfulEnrollment()))
          );
        }
      } else if (enrollmentId) {
        const { data } = await unEnroll({
          variables: {
            enrollmentId: enrollmentId
          },
          refetchQueries: ['GetEvent']
        });
        if (data.waitingListId) {
          dispatch(
            AlertActions.success(
              t(translations.pages.events.alerts.successfulUnenrollmentFromWaitingList())
            )
          );
        } else {
          dispatch(
            AlertActions.success(t(translations.pages.events.alerts.successfulUnenrollment()))
          );
        }
      }
    } catch (error) {
      const typedError = error as ApolloError;
      if (!enrollmentId) {
        if (typedError.graphQLErrors[0]?.extensions?.code === '412') {
          if (
            (event &&
              (event.registrationPeriodStart as unknown as String) > new Date().toISOString()) ||
            (group?.event &&
              (group.event.registrationPeriodStart as unknown as String) > new Date().toISOString())
          ) {
            dispatch(
              AlertActions.error(t(translations.pages.events.alerts.failedEnrollmentTooEarly()))
            );
          } else {
            dispatch(
              AlertActions.error(
                t(translations.pages.events.alerts.failedEnrollmentForPreconditionFailed())
              )
            );
          }
        } else {
          dispatch(AlertActions.error(t(translations.pages.events.alerts.failedEnrollment())));
        }
      } else if (enrollmentId) {
        dispatch(AlertActions.error(t(translations.pages.events.alerts.failedUnenrollment())));
      }
    }
  };

  const determineGroupToMark = useCallback(
    (event: ResolvedEvent) => {
      if (markedGroup || !event.groups?.length) {
        return;
      }
      if (event.enrollmentId) {
        const groupWithEnrollment = event.groups.find(
          group =>
            group.enrollments?.some(e => e.id === event.enrollmentId) ||
            group.preCalcEnrollments?.some(e => e.id === event.enrollmentId)
        );

        setMarkedGroup(groupWithEnrollment?.id || event.groups[0].id);
      } else {
        setMarkedGroup(event.groups[0].id);
      }
    },
    [markedGroup]
  );

  useEffect(() => {
    getEvent.data?.getEvent && determineGroupToMark(getEvent.data?.getEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEvent.data?.getEvent]);

  function shouldEnrollmentOverviewBeVisible(
    event: ResolvedEvent,
    canEdit: boolean,
    readOnlyAdmin: boolean
  ) {
    const hasMarkedGroupOrEvent =
      markedGroup !== null || event.waitingType === EventWaitingType.EVENT;
    const hasPermission = canEdit || readOnlyAdmin || event.isParticipantListVisible;
    const isNotMobile = !isMobile;

    return hasMarkedGroupOrEvent && hasPermission && isNotMobile;
  }

  const Body = () => {
    return (
      <GQLGuard
        query={getEvent}
        onError={error => {
          dispatch(AlertActions.error(t(translations.pages.events.alerts.failedLoadEvent())));
          navigate('/events', { replace: true });
          return null;
        }}
      >
        {(eventById: GetEventById) => {
          const event = eventById.getEvent;
          const canEdit = isOrganizer(
            currentUser,
            authorRoles,
            event.organizers,
            true,
            event.hostId
          );
          const readOnlyAdmin = isOrganizer(
            currentUser,
            authorRoles,
            event.organizers,
            false,
            event.hostId
          );
          return (
            <>
              <ConfirmationDialog
                open={enrollingState === 'ENROLLING'}
                title={t(translations.pages.events.confirmationDialog.enrollTitle())}
                onClose={() => {
                  setEnrollingState(undefined);
                }}
                onConfirm={() => {
                  handleEnrollment(event, groupRef);
                  setEnrollingState(undefined);
                }}
              >
                <div>{t(translations.pages.events.confirmationDialog.enrollText())}</div>
              </ConfirmationDialog>
              <ConfirmationDialog
                open={enrollingState === 'UNENROLLING'}
                title={t(translations.pages.events.confirmationDialog.unenrollTitle())}
                onClose={() => {
                  setEnrollingState(undefined);
                }}
                onConfirm={() => {
                  handleEnrollment(event, groupRef, event.enrollmentId);
                  setEnrollingState(undefined);
                }}
              >
                <div>{t(translations.pages.events.confirmationDialog.unenrollText())}</div>
              </ConfirmationDialog>
              <Grid container gap={2}>
                <Grid component={Paper} item xs={12}>
                  <EventOrganizationInfo
                    eventData={event}
                    setEnrollingState={setEnrollingState}
                    setEnrollingType={setEnrollingType}
                    canEdit={canEdit}
                    readOnlyAdmin={readOnlyAdmin}
                    refetch={refetch}
                  />
                </Grid>
                <Grid component={Paper} item xs={12}>
                  <EventGroupOverview
                    eventData={event}
                    setGroupRef={setGroupRef}
                    setEnrollingState={setEnrollingState}
                    setEnrollingType={setEnrollingType}
                    markedGroup={markedGroup}
                    canEdit={canEdit}
                    setMarkedGroup={setMarkedGroup}
                  />
                </Grid>
                {shouldEnrollmentOverviewBeVisible(event, canEdit, readOnlyAdmin) && (
                  <Grid component={Paper} item xs={12}>
                    <EventEnrollmentOverview
                      eventData={event}
                      markedGroup={markedGroup}
                      canEdit={canEdit}
                    />
                  </Grid>
                )}
                {isMobile && (
                  <Alert severity="info" style={{ margin: 10 }}>
                    {t(
                      translations.pages.events.eventDetailPage.enrollmentOverviewSwitchToDesktopInfo()
                    )}
                  </Alert>
                )}
              </Grid>
            </>
          );
        }}
      </GQLGuard>
    );
  };

  if (isMobile) {
    return (
      <Grid>
        <Tabs value={'overview'} indicatorColor="primary">
          <Tab
            value={undefined}
            icon={<ChevronLeft />}
            label={t(translations.common.back())}
            style={{ fontSize: 10, flex: 1 }}
            onClick={() => navigate('/events')}
          />
          <Tab
            value={'overview'}
            icon={<Info />}
            label={t(translations.pages.studentView.tabs.selectionInformationMobile())}
            style={{ fontSize: 10, flex: 1 }}
          />
        </Tabs>
        <Body />
      </Grid>
    );
  }

  return <Body />;
};
